(ns manage.adset.modal.view
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Divider$default" :as Divider]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/ListItemIcon$default" :as ListItemIcon]
            ["@mui/material/MenuItem$default" :as MenuItem]
            ["@mui/material/Select$default" :as Select]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Typography$default" :as Typography]
            ["react-hook-form" :as rhf]
            [cljs-bean.core :refer [->clj]]
            [clojure.set :as set]
            [clojure.string :as str]
            [common.flags :refer [use-flag]]
            [common.form :as form]
            [common.modal.view :refer [defmodal modal-v2]]
            [common.queries :refer [use-mutation]]
            [common.resource.query.adset :as q]
            [common.util :refer [string-number?]]
            [common.widgets.checkbox :refer [checkbox]]
            [common.widgets.daterange-picker :as picker]
            [common.widgets.dropdown :refer [dropdown]]
            [common.widgets.form-field :refer [form-field hint]]
            [common.widgets.input :refer [input]]
            [manage.adset.business-logic :as business-logic]
            [manage.adset.modal.db :as form.db]
            [manage.adset.modal.hooks :as hooks]
            [manage.adset.util :as adset-util]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(def production-solution-options
  [{:value "create"
    :label "Creative Optimizations"
    :icon "segment_rounded"}
   {:value "hogarth"
    :label "WPP Production Studio (Hogarth)"
    :icon "segment_rounded"}])

(def shortcuts-items
  [{:id "30-days"
    :label "30 days"
    :getValue (fn [value]
                (let [[start _] value
                      end (-> start (.clone) (.add 30 "days"))]
                  #js [start end]))
    :isDisabled (fn [value]
                  (let [[start _] value]
                    (nil? start)))}
   {:id "60-days"
    :label "60 days"
    :getValue (fn [value]
                (let [[start _] value
                      end (-> start (.clone) (.add 60 "days"))]
                  #js [start end]))
    :isDisabled (fn [value]
                  (let [[start _] value]
                    (nil? start)))}
   {:id "90-days"
    :label "90 days"
    :getValue (fn [value]
                (let [[start _] value
                      end (-> start (.clone) (.add 90 "days"))]
                  #js [start end]))
    :isDisabled (fn [value]
                  (let [[start _] value]
                    (nil? start)))}
   {:id "6-months"
    :label "6 months"
    :getValue (fn [value]
                (let [[start _] value
                      end (-> start (.clone) (.add 6 "months"))]
                  #js [start end]))
    :isDisabled (fn [value]
                  (let [[start _] value]
                    (nil? start)))}
   {:id "1-year"
    :label "1 year"
    :getValue (fn [value]
                (let [[start _] value
                      end (-> start (.clone) (.add 1 "year"))]
                  #js [start end]))
    :isDisabled (fn [value]
                  (let [[start _] value]
                    (nil? start)))}])

(defui date-fields []
  ($ form-field
     {:label "Planned dates"
      :required? true
      :err-msg (form.db/use-error-message [:planned-dates])}
     ($ picker/daterange-picker
        (merge (-> (form.db/use-bind-control [:planned-dates])
                   (set/rename-keys {:on-change :onChange
                                     :on-blur :onBlur
                                     :on-focus :onFocus}))
               {:shortcuts shortcuts-items
                :placeholder "Select start date - Select duration"
                :disablePast true
                :slotProps #js {:layout #js {:sx #js {".MuiPickersLayout-shortcuts"
                                                      #js {:gridArea "2 / 2 / 3"
                                                           :borderRight "none"
                                                           :borderLeft "1px solid"
                                                           :borderColor "grey.70"
                                                           :paddingLeft "8px"
                                                           :paddingRight 0}

                                                      ".MuiPickersLayout-contentWrapper"
                                                      #js {:gridArea "2 / 1"}}}}}))))

(defui region-field [{:keys [multiple]}]
  (let [options (use-subscribe [:adset-create/country-options])
        {:keys [on-change value] :as control} (form.db/use-bind-control [:countries])]
    ($ form-field
       {:label "Region"
        :required? (not multiple)
        :err-msg (form.db/use-error-message [:countries])}
       (r/as-element
        [dropdown (merge control
                         {:options options
                          :multi? multiple
                          :value (if multiple
                                   (or (to-array value) [])
                                   (or (first value) ""))
                          :on-change (fn [value]
                                       (on-change
                                        (if multiple
                                          value
                                          [value])))
                          :placeholder (when (and multiple (empty? value))
                                         "Global")
                          :disable-portal false
                          :render-checkboxes? multiple})])
       ($ hint "Where are you planning to activate the adset?"))))

(defui production-solution-field []
  (let [options production-solution-options
        {:keys [on-change] :as control} (form.db/use-bind-control [:production-solution])]
    ($ form-field
       {:label "Production Solution"
        :required? true}
       ($ Select (merge control
                        {:value (or (:value control) (-> options first :value))
                         :size "small"
                         :render-value (fn [value]
                                         (let [{:keys [label icon]} (first (filter #(= (:value %) value) options))]
                                           ($ Stack {:direction "row"
                                                     :gap 1}
                                              ($ Icon icon)
                                              ($ Typography label))))
                         :on-change (fn [^js event]
                                      (on-change (-> event .-target .-value)))
                         :sx #js {:width "100%"}})
          (for [o options]
            ($ MenuItem {:key (:value o)
                         :value (:value o)}
               ($ ListItemIcon
                  ($ Icon "segment_rounded"))
               (:label o))))
       ($ hint "Which tool will be used for content production?"))))

(defui workflow-field []
  (let [options (hooks/use-workflow-options)
        {:keys [on-change] :as control} (form.db/use-bind-control [:workflow])]
    ($ form-field
       {:label "Workflow"
        :required? true
        :err-msg (form.db/use-error-message [:workflow])}
       ($ Select (merge control
                        {:size "small"
                         :render-value (fn [value]
                                         (let [{:keys [label icon]} (first (filter #(= (:value %) value) options))]
                                           ($ Stack {:direction "row"
                                                     :gap 1}
                                              ($ Icon icon)
                                              ($ Typography label))))
                         :on-change (fn [^js event]
                                      (on-change (-> event .-target .-value)))
                         :sx #js {:width "100%"}
                         :data-testid "adset-workflow-select"})
          (for [w options]
            ($ MenuItem {:key (:value w)
                         :value (:value w)}
               ($ ListItemIcon
                  ($ Icon (:icon w)))
               (:label w))))
       ($ hint "Which workflow do you want to use to configure your adset?"))))

(defui modal-content []
  (let [channel (use-subscribe [:form.adset-create/value [:channel]])
        workflow (use-subscribe [:form.adset-create/value [:workflow]])
        production-studio-enabled? (use-flag :production-studio-adsets)
        channel-options (hooks/use-channel-options)
        workflow-options (hooks/use-workflow-options)]
    (uix/use-effect (fn []
                      (rf/dispatch [:form.adset-create/on-change [:countries] []])
                      (rf/dispatch [:form.adset-create/on-change [:workflow]
                                    (business-logic/auto-select-workflow workflow-options)]))
                    [channel workflow-options])
    ($ Stack {:gap 3
              :sx #js {:py 2
                       :minWidth 580}}
       ($ form-field
          {:label "Name"
           :required? true
           :err-msg (form.db/use-error-message [:name])}
          ($ input
             (merge (form.db/use-bind-control [:name])
                    {:type "text"
                     :data-testid "adset-name-input"
                     :placeholder "Type a short and brief name"
                     :autoFocus true})))
       ($ form-field
          {:label "Channel"
           :required? true
           :err-msg (form.db/use-error-message [:channel])}
          (let [{:keys [on-change] :as control} (form.db/use-bind-control [:channel])]
            ($ Select (merge control
                             {:value (or (:value control) "")
                              :size "small"
                              :render-value (fn [value]
                                              (let [label (:label (first (filter #(= (:value %) value) channel-options)))]
                                                ($ Stack {:direction "row"
                                                          :gap 1}
                                                   ($ adset-util/channel-icon {:channel value})
                                                   ($ Typography label))))
                              :on-change (fn [^js event]
                                           (on-change (-> event .-target .-value)))
                              :sx #js {:width "100%"}
                              :data-testid "adset-channel-select"})
               (for [chan channel-options]
                 ($ MenuItem {:key (:value chan)
                              :value (:value chan)}
                    ($ ListItemIcon
                       ($ adset-util/channel-icon {:channel (:value chan)}))
                    (:label chan)))))
          ($ hint "What channel are you targeting with this adset?"))

       ($ date-fields)

       ($ form-field
          {:label "Target impressions"
           :required? true
           :err-msg (form.db/use-error-message [:target-impressions])}
          ($ input
             (merge (form.db/use-bind-control [:target-impressions]
                                              {:f #(if (and (some? %) (string-number? %))
                                                     (int %)
                                                     %)})
                    {:type "number"
                     :data-testid "adset-target-impressions-input"
                     :placeholder "eg. 100,000"})))

       ($ region-field
          {:multiple (not= channel "ctv")})

       (when (> (count workflow-options) 1)
         ($ :<>
            ($ Divider)
            ($ workflow-field)))

       (when (and (#{"display" "video" "meta"} channel)
                  (= workflow "ad-variants")
                  production-studio-enabled?)
         ($ production-solution-field)))))

(defmodal create-adset :adset/create-adset
  []
  (uix/use-effect (fn []
                    (rf/dispatch [:form.adset-create/init])
                    (fn []
                      (rf/dispatch [:form.adset-create/destroy])))
                  [])
  (let [{:keys [advertiser-id campaign-id] :as url-context} (use-subscribe [:url-context])
        {:keys [create-next] :as form-values} (use-subscribe [:form.adset-create/value []])
        {create-adset :mutate} (use-mutation (cond-> (q/create-adset-mutation {:campaign-id campaign-id})
                                               (use-flag :add-google-cm-data-source)
                                               (q/then-add-google-cm-data-source {:advertiser-id advertiser-id})))

        on-create-success (fn [^js response]
                            (rf/dispatch [:form.adset-create/destroy])
                            (if create-next
                              (rf/dispatch [:form.adset-create/init (assoc form.db/initial-value :create-next true)])
                              (do (rf/dispatch [:modal/close])
                                  (adset-util/navigate-to-landing-page (.-workflow response)
                                                                       (assoc url-context :adset-id (.-id response))))))]
    ($ modal-v2
       {:title "Create adset"
        :footer ($ Stack {:direction "row"
                          :justifyContent "space-between"
                          :align "right"
                          :sx #js {:width "100%"}}
                   ($ checkbox
                      (merge (form.db/use-bind-control [:create-next])
                             {:label "Create another adset"
                              :data-testid :create-another-adset-after}))
                   ($ Stack {:direction "row" :gap 1}
                      ($ Button {:variant "text"
                                 :sx #js {:color "text.primary"}
                                 :on-mouse-down #(rf/dispatch [:modal/close])}
                         "Cancel")
                      ($ Button {:variant "contained"
                                 :onClick (fn []
                                            (create-adset (business-logic/to-adset-spec form-values)
                                                          #js {:onSuccess on-create-success}))
                                 :disabled (use-subscribe [:form.adset-create/errors?])}
                         "Create")))}
       ($ modal-content))))

(defmodal edit-adset :adset/edit
  [{:keys [adset]}]
  (let [automated-activation? (use-flag :automated-activation)
        social-adset? (contains? #{"meta" "tiktok"} (:channel adset))
        {:keys [on-submit control]} (hooks/use-edit-form adset)]
    ($ modal-v2
       {:title "Edit adset"
        :footer ($ Stack
                   {:direction "row"
                    :gap 1
                    :sx #js {:padding 0 :alignItems "right"}}
                   ($ Button
                      {:variant "text"
                       :sx #js {:color "text.primary"}
                       :onClick #(rf/dispatch [:modal/close])}
                      "Cancel")
                   ($ Button
                      {:variant "contained"
                       :onClick on-submit
                       :disabled (str/blank? name)}
                      "Save"))}
       ($ Stack {:gap 3
                 :sx #js {:py 2 :minWidth 480}}
          ($ form-field
             {:label "Name"
              :required? true}
             ($ form/form-text-field {:name "name"
                                      :control control}))
          ($ rhf/Controller
             {:name "plannedDates"
              :control control
              :render (fn [^js props]
                        (let [{field-control :field
                               {:keys [error]} :fieldState
                               :as props} (->clj props)]
                          ($ form-field
                             {:label "Planned dates"
                              :required? true}
                             ($ picker/daterange-picker
                                (merge field-control
                                       {:shortcuts shortcuts-items
                                        :placeholder "Select start date - Select duration"
                                        :disablePast true
                                        :helperText (form/error-message error)
                                        :error (boolean error)
                                        :slotProps #js {:layout #js {:sx #js {".MuiPickersLayout-shortcuts"
                                                                              #js {:gridArea "2 / 2 / 3"
                                                                                   :borderRight "none"
                                                                                   :borderLeft "1px solid"
                                                                                   :borderColor "grey.70"
                                                                                   :paddingLeft "8px"
                                                                                   :paddingRight 0}
                                                                              ".MuiPickersLayout-contentWrapper"
                                                                              #js {:gridArea "2 / 1"}}}}})))))})
          ($ form-field
             {:label "Target impressions"
              :required? true}
             ($ form/form-text-field {:name "targetImpressions"
                                      :on-change-fn #(if (some-> % string-number?)
                                                       (int %)
                                                       %)
                                      :control control
                                      :field-props {:type "number"}}))
          (when automated-activation?
            ($ adset-util/automated-workflow-switches
               {:control control
                :social-adset? social-adset?}))))))
